import { captureError } from '@smg-real-estate/browser-rum';
import { type ActionContext } from 'vuex';

import { postInsertionSubmittal, putInsertion } from '@/api/insertions';
import { applyProjectBoostToListing } from '@/helpers/projectBoost';
import { ApiCallStatus, type InsertionsState } from '@/store/insertions/InsertionsStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { type Insertion } from '@/types';

/**
 * NC Boost lives on the listing rather than on the toplisting service, so booking or
 * cancelling it means writing `newConstructionData.projectType` and republishing.
 *
 * Returns the published insertion, or `undefined` when the update failed.
 */
export async function publishProjectBoostChange(
  context: ActionContext<InsertionsState, any>,
  payload: { insertion: Insertion; enabled: boolean },
): Promise<Insertion | undefined> {
  const { insertion, enabled } = payload;
  const sourcePlatform = useUserStore().sourcePlatform;

  context.commit('setApiCallStatus', ApiCallStatus.IN_PROGRESS);
  try {
    /*
     * The manage-ads list is filled from /insertions/search, which returns a reduced listing.
     * Putting that back would drop fields the API requires, so the full insertion is loaded
     * first and the project type is applied on top of it.
     */
    const currentInsertion: Insertion | undefined = await context.dispatch('refetchInsertionById', {
      userId: insertion.userId,
      insertionId: insertion.id,
    });
    const listing = applyProjectBoostToListing(
      currentInsertion?.listing ?? insertion.listing,
      enabled,
    );

    await putInsertion(insertion.userId, insertion.id, listing, sourcePlatform);
    const publishedInsertion = await postInsertionSubmittal(
      insertion.userId,
      insertion.id,
      sourcePlatform,
    );

    context.commit(
      'setListingActionSuccess',
      enabled
        ? 'manageAdsPage.toasts.projectBoostActivated'
        : 'manageAdsPage.toasts.projectBoostDeactivated',
    );
    context.commit('setApiCallStatus', ApiCallStatus.SUCCESS);

    return publishedInsertion;
  } catch (error) {
    context.commit('setApiCallStatus', ApiCallStatus.ERROR);
    captureError(error);

    return undefined;
  }
}
